import { SafeHtml } from "@angular/platform-browser";

/**
 * Image breakpoints for which srcset rules are generated
 */
export const IMAGE_BREAKPOINTS = [128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840];

/**
 * Carousel tile image
 * - suitable to be used with NgOptimizedImage
 */
export interface YsCarouselTileImage {
  /**
   * image source, typically URL or data
   * - used as ngSrc for NgOptimizedImage
   */
  src: string;
  // /**
  //  * The intrinsic width of the image in pixels. Must be an integer without a unit.
  //  */
  // width: number;
  // /**
  //  * The intrinsic height of the image, in pixels. Must be an integer without a unit.
  //  */
  // height: number;
  /**
   * image style, defaults to "width:100%;height:auto;object-fit:cover;"
   * @deprecated use objectPosition instead
   */
  // style?: string;
  /**
   * image object position, defaults to "center center"
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-position
   */
  objectPosition?: string;
  /**
   * image class, defaults to "ys-carousel-tile-image"
   */
  class?: string;
  /**
   * Alternative text description of the image.
   */
  alt?: string;
  // /**
  //  * srcset provides one or more strings, indicating possible image sources for the user agent to use. 
  //  * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-srcset
  //  */
  // srcset?: string[];
  // /**
  //  * custom breakpoints for which srcset rules are generated
  //  */
  // breakpoints?: number[];
  /**
   * sizes provides one or more strings, indicating a set of source sizes.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-sizes
   */
  sizes?: string[];

  /**
   * The desired loading behavior (lazy, eager, or auto).
   * 
   * Setting images as loading='eager' or loading='auto' marks them as non-priority images. 
   * Avoid changing this input for priority images.
   * 
   * @see https://angular.io/api/common/NgOptimizedImage#properties
   */
  loading?: 'lazy' | 'eager' | 'auto';

  /**
   * Indicates whether this image should have a high priority.
   * @see https://angular.io/api/common/NgOptimizedImage#properties
   * @default false
   */
  priority?: boolean;

  // /**
  //  * Sets the image to "fill mode", which eliminates the height/width requirement and adds styles such that the image fills its containing element.
  //  * @see https://angular.io/api/common/NgOptimizedImage#properties
  //  * @default false
  //  * @note This is in Developer Preview with Angular v15
  //  */
  // fill?: boolean;
}



/**
 * Carousel tile definition
 * - a maximum of 10 tiles is supported
 */
export interface YsCarouselTile {
  /**
   * applicable class attribute for tiles
   */
  styleClass?: string,
  /**
   * inner HTML, e.g. picture element, mutual exclusive with img
   */
  html?: string,
  /**
   * image definition to be used with NgOptimizedImage, mutual exclusive with html
   */
  img?: YsCarouselTileImage,
  /**
   * external reference
   */
  extRef?: string,
}

/**
 * Internal representation of carousel tile
 */
export interface YsCarouselTileInternal {
  /**
   * applicable class attribute for tiles
   */
  styleClass?: string,

  /**
   * src attribute definition
   */
  src?: string,

  /**
   * width attribute definition
   */
  width?: number,

  /**
   * height attribute definition
   */
  height?: number,

  /**
   * alt attribute definition
   */
  alt?: string,

  /**
   * style attribute definition
   */
  style?: string,

  /**
   * class attribute definition
   */
  class?: string,

  /**
   * srcset attribute definition
   */
  srcset?: string,

  /**
   * sizes attribute definition
   */
  sizes?: string,

  /**
   * loading attribute definition
   */
  loading?: 'lazy' | 'eager' | 'auto',

  /**
   * priority attribute definition
   */
  priority?: boolean,

  /**
   * fill attribute definition
   */
  fill?: boolean,

  /**
   * Cross origin attribute definition
   */
  crossOrigin?: string,

  /**
   * Tile inner HTML for particular purposes, sanitized by DomSanitizer
   */
  safeHtml?: SafeHtml,        // inner HTML, e.g. picture element, created on initialization

}

export interface YsCarouselConfig {
  autoplay?: boolean, // whether to start running or not, defaults to true, if more than one tile
  transitionTimeMs?: number, // animation transition time, defaults to 0 => disabled
  presentationTimeMs?: number, // time a tile is presented, must exceed transitionTimeMs, defaults to 10 seconds
  progressIndicator?: boolean, // whether progress bar shall be presented or not, defaults to true
  pagination?: boolean, // whether pagination bubbles shall be presented or not, defaults to true.
  controls?: boolean, // whether forward and backward button shall be presented or not, defaults to true
  playButton?: boolean, // whether play button shall be presented on stopped auto-presentation. Defaults to false.
  onClickPausePresentation?: boolean, // whether click on presentation pane shall pause presentation, defaults to true
}


export const initialYsCarouselConfig: YsCarouselConfig = {
  autoplay: true,
  transitionTimeMs: 1000,
  presentationTimeMs: 15000,
  progressIndicator: false,
  pagination: true,
  controls: true,
  playButton: true,
  onClickPausePresentation: true,
};


export const initialYsDiaShowConfig: YsCarouselConfig = {
  autoplay: true,
  transitionTimeMs: 1000,
  presentationTimeMs: 15000,
  progressIndicator: true,
  pagination: false,
  controls: true,
  playButton: true,
  onClickPausePresentation: true,
};
export enum YsCarouselState {
  PLAYING = "PLAYING",
  PAUSED = "PAUSED",
  NOT_PLAYING = "NOT_PLAYING"
}

export interface YsCarouselClickedEvent {
  index: number,
  tile: YsCarouselTile,
  state: YsCarouselState
}

export interface YsCarouselAnimation {
  value: string, // animation operation, one of slideTo, slideReset, stepTo 
  params: {
    sliderPositionPercent: number, // slider position in percent 
    index: number, // active index
    tileCount: number, // total number of tiles
    transitionTimeMs: number, // transition time in ms
  }
}

/**
 * swipe support
 */
export interface SwipeStart {
  coord: [number, number];
  time: number;
}
